import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaBars, FaTimes, FaChevronDown, FaMapMarkerAlt, FaBookmark, FaUser, FaSignOutAlt, FaSearch, FaClock, FaFire } from 'react-icons/fa';
import { submitSearchQuery, getUserBookmarks } from '../services/api';
import PropertyImage from './PropertyImage';

/* ── Recently Viewed helpers (shared with Hero) ── */
const RECENT_KEY = 'mb_recent_properties';
const getRecentlyViewed = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; } };
const addRecentlyViewed = (property) => {
  const prev = getRecentlyViewed().filter(p => p.slug !== property.slug);
  const updated = [{ slug: property.slug, title: property.title, location: property.location, price: property.price, images: property.images, builderName: property.builderName, _id: property._id }, ...prev].slice(0, 5);
  localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
};

const cities = ['Mumbai', 'Pune', 'Bengaluru', 'Hyderabad', 'Ahmedabad', 'Chennai', 'Delhi', 'Kolkata', 'Thane', 'Goa'];

const Navbar = ({ user, onLoginClick, onSignUpClick, onLogout, selectedCity, setSelectedCity }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [defaultResults, setDefaultResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [recentResults, setRecentResults] = useState([]);
  const [bookmarkCount, setBookmarkCount] = useState(0);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const cityRef = useRef(null);
  const userMenuRef = useRef(null);
  const searchRef = useRef(null);
  const moreMenuRef = useRef(null);

  // Use internal state if props not provided (for non-homepage pages)
  const [internalCity, setInternalCity] = useState('Mumbai');
  const currentCity = selectedCity || internalCity;
  const setCurrentCity = setSelectedCity || setInternalCity;

  // Fetch default properties on mount + load recently viewed
  useEffect(() => {
    const fetchDefaultProperties = async () => {
      try {
        const results = await submitSearchQuery('');
        setDefaultResults(Array.isArray(results) ? results : []);
      } catch (err) {
        console.error('Failed to fetch default properties:', err);
      }
    };
    fetchDefaultProperties();
    setRecentResults(getRecentlyViewed());
  }, []);

  // Fetch user bookmarks count
  useEffect(() => {
    const fetchBookmarkCount = async () => {
      if (user) {
        try {
          const response = await getUserBookmarks();
          if (response.success) {
            setBookmarkCount(response.bookmarks?.length || 0);
          }
        } catch (err) {
          console.error('Failed to fetch bookmarks:', err);

          // If session expired, clear auth state
          if (err.message && err.message.includes('Session expired')) {
            console.warn('🔄 Session expired - User will be logged out');
            if (onLogout) {
              onLogout(); // This will clear user state in App.js
            }
          }
        }
      } else {
        setBookmarkCount(0); // Reset count when logged out
      }
    };
    fetchBookmarkCount();
  }, [user, onLogout]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (cityRef.current && !cityRef.current.contains(e.target)) setCityOpen(false);
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowSearchResults(false);
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) setMoreMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Handle search with debouncing
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        handleSearch(searchQuery);
      } else {
        setSearchResults([]);
        setShowSearchResults(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  const handleSearch = async (query) => {
    setSearchLoading(true);
    try {
      const results = await submitSearchQuery(query);
      setSearchResults(Array.isArray(results) ? results.slice(0, 8) : []);
      setShowSearchResults(true);
    } catch (err) {
      console.error('Search error:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const buildDropdownSections = () => {
    const recentSlugs = new Set(recentResults.map(r => r.slug));
    const freshDefaults = defaultResults.filter(p => !recentSlugs.has(p.slug));
    return { recent: recentResults, freshDefaults };
  };

  const handleSelectProperty = (property) => {
    addRecentlyViewed(property);
    setRecentResults(getRecentlyViewed());
    const slug = property.slug || property._id;
    setSearchQuery('');
    setShowSearchResults(false);
    navigate(`/projects/${slug}`);
  };

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    if (!searchQuery.trim()) return;
    
    // Redirect to search results page with query
    const queryToSearch = searchQuery.trim();
    setShowSearchResults(false);
    setSearchQuery('');
    navigate(`/search?q=${encodeURIComponent(queryToSearch)}`);
  };

  return (
    <nav className="bg-white shadow-md fixed top-0 left-0 right-0 z-[9999999]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img src="/mainlogo.png" alt="MetroBrix" className="h-12 w-auto" />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-6 flex-1 max-w-2xl mx-auto">
            {/* City Selector */}
            <div ref={cityRef} className="relative">
              <button
                onClick={() => setCityOpen(!cityOpen)}
                className="flex items-center space-x-1 text-sm font-medium text-gray-700 cursor-pointer hover:text-red-500 transition"
              >
                <FaMapMarkerAlt className="text-red-500 text-sm" />
                <span>{currentCity}</span>
                <FaChevronDown className={`text-xs transition-transform ${cityOpen ? 'rotate-180' : ''}`} />
              </button>
              {cityOpen && (
                <div className="absolute top-full left-0 mt-2 w-44 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50">
                  {cities.map((city) => (
                    <button
                      key={city}
                      onClick={() => {
                        setCurrentCity(city);
                        setCityOpen(false);
                        navigate(`/in/${city.toLowerCase()}`);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-red-50 hover:text-red-500 transition ${currentCity === city ? 'text-red-500 font-semibold bg-red-50' : 'text-gray-700'}`}
                    >
                      <FaMapMarkerAlt className="inline mr-2 text-xs" />
                      {city}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Search Bar */}
            <form onSubmit={handleSearchSubmit} className="relative flex-1" ref={searchRef}>
              <div className="relative">
                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => { setShowSearchResults(true); }}
                  placeholder="Search properties, builders, localities..."
                  className="w-full pl-11 pr-24 py-2.5 bg-gray-50 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white transition"
                />
                <button
                  type="submit"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-red-500 hover:bg-red-600 text-white px-4 py-1.5 rounded-full text-xs font-semibold transition"
                >
                  Search
                </button>
              </div>

              {/* Search Dropdown */}
              {showSearchResults && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 z-[9999] max-h-[420px] overflow-y-auto">
                  {searchLoading && (
                    <div className="flex justify-center py-4">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-500" />
                    </div>
                  )}

                  {/* Typed search results */}
                  {!searchLoading && searchQuery.trim().length >= 2 && searchResults.length > 0 && (
                    <>
                      <div className="px-4 pt-3 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Search Results</div>
                      {searchResults.map(p => (
                        <button key={p._id || p.slug} onMouseDown={(e) => { e.preventDefault(); handleSelectProperty(p); }} className="w-full px-4 py-2.5 hover:bg-gray-50 transition flex items-start space-x-3 text-left">
                          <PropertyImage property={p} className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden" imgClassName="w-full h-full object-cover" />
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-gray-900 truncate">{p.title}</h4>
                            {p.builderName && <p className="text-xs text-gray-400 truncate">by {p.builderName}</p>}
                            {p.location && <p className="text-xs text-gray-500 flex items-center mt-0.5"><FaMapMarkerAlt className="text-red-400 mr-1 text-[9px]" />{p.location}</p>}
                            {p.price && <p className="text-xs font-bold text-red-500 mt-0.5">₹{p.price}</p>}
                          </div>
                        </button>
                      ))}
                    </>
                  )}

                  {/* No results */}
                  {!searchLoading && searchQuery.trim().length >= 2 && searchResults.length === 0 && (
                    <p className="text-center text-sm text-gray-400 py-6">No results found</p>
                  )}

                  {/* Default suggestions — recently viewed + all */}
                  {!searchLoading && searchQuery.trim().length < 2 && (() => {
                    const { recent, freshDefaults } = buildDropdownSections();
                    return (
                      <>
                        {recent.length > 0 && (
                          <>
                            <div className="px-4 pt-3 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1"><FaClock className="text-[9px]" /> Recently Viewed</div>
                            {recent.map(p => (
                              <button key={p.slug} onMouseDown={(e) => { e.preventDefault(); handleSelectProperty(p); }} className="w-full px-4 py-2.5 hover:bg-gray-50 transition flex items-start space-x-3 text-left">
                                <PropertyImage property={p} className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden" imgClassName="w-full h-full object-cover" />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <FaClock className="text-gray-300 text-[10px] flex-shrink-0" />
                                    <h4 className="text-sm font-semibold text-gray-900 truncate">{p.title}</h4>
                                  </div>
                                  {p.builderName && <p className="text-xs text-gray-400 truncate">by {p.builderName}</p>}
                                  {p.location && <p className="text-xs text-gray-500 flex items-center mt-0.5"><FaMapMarkerAlt className="text-red-400 mr-1 text-[9px]" />{p.location}</p>}
                                  {p.price && <p className="text-xs font-bold text-red-500 mt-0.5">₹{p.price}</p>}
                                </div>
                              </button>
                            ))}
                          </>
                        )}
                        {freshDefaults.length > 0 && (
                          <>
                            <div className="px-4 pt-3 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1"><FaFire className="text-red-400 text-[9px]" /> Suggested Properties</div>
                            {freshDefaults.map(p => (
                              <button key={p._id || p.slug} onMouseDown={(e) => { e.preventDefault(); handleSelectProperty(p); }} className="w-full px-4 py-2.5 hover:bg-gray-50 transition flex items-start space-x-3 text-left">
                                <PropertyImage property={p} className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden" imgClassName="w-full h-full object-cover" />
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-sm font-semibold text-gray-900 truncate">{p.title}</h4>
                                  {p.builderName && <p className="text-xs text-gray-400 truncate">by {p.builderName}</p>}
                                  {p.location && <p className="text-xs text-gray-500 flex items-center mt-0.5"><FaMapMarkerAlt className="text-red-400 mr-1 text-[9px]" />{p.location}</p>}
                                  {p.price && <p className="text-xs font-bold text-red-500 mt-0.5">₹{p.price}</p>}
                                </div>
                              </button>
                            ))}
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </form>
          </div>

          {/* Bookmarks Icon - Visible on mobile */}
          <Link
            to="/profile"
            className="hidden items-center space-x-1.5 text-sm font-medium text-gray-700 hover:text-red-500 transition relative"
          >
            <FaBookmark className="text-red-500" size={20} />
            {user && bookmarkCount > 0 && (
              <span className="relative -top-[-6px] -right-[-10px] bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                {bookmarkCount}
              </span>
            )}
          </Link>


          <div className="hidden lg:flex items-center space-x-4">
            {user ? (
              /* Logged-in state */
              <div ref={userMenuRef} className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-full transition"
                >
                  <div className="w-7 h-7 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                    {user.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <span className="text-sm font-medium text-gray-700 max-w-[100px] truncate">{user.name}</span>
                  <FaChevronDown className={`text-xs text-gray-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {userMenuOpen && (
                  <div className="absolute top-full right-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-900 truncate">{user.name}</p>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                    <Link
                      to="/profile"
                      onClick={() => setUserMenuOpen(false)}
                      className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-red-50 hover:text-red-500 flex items-center justify-between"
                    >
                      <div className="flex items-center space-x-2">
                        <FaBookmark className="text-red-400" />
                        <span>My Bookmarks</span>
                      </div>
                      {bookmarkCount > 0 && (
                        <span className="bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                          {bookmarkCount}
                        </span>
                      )}
                    </Link>
                    <Link
                      to="/profile"
                      className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-red-50 hover:text-red-500 flex items-center space-x-2"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <FaUser className="text-gray-400" />
                      <span>My Profile</span>
                    </Link>
                    <button
                      onClick={() => { onLogout(); setUserMenuOpen(false); }}
                      className="w-full text-left px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 flex items-center space-x-2 border-t border-gray-100"
                    >
                      <FaSignOutAlt />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* Not logged-in state */
              <>
                <button
                  onClick={onLoginClick}
                  className="text-sm font-medium text-gray-700 hover:text-red-500 transition"
                >
                  Login
                </button>
                <button
                  onClick={onSignUpClick}
                  className="bg-red-500 hover:bg-red-600 text-white px-5 py-2 rounded-full text-sm font-semibold transition shadow-md"
                >
                  Sign Up Free
                </button>
              </>
            )}
            <Link
              to="/profile"
              className="hidden lg:flex items-center space-x-1.5 text-sm font-medium text-gray-700 hover:text-red-500 transition relative"
            >
              <FaBookmark className="text-red-500" />
              {/* <span>My Bookmarks</span> */}
              {user && bookmarkCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
                  {bookmarkCount}
                </span>
              )}
            </Link>
          </div>
          {/* More Menu Dropdown - Hidden on mobile */}
          <div ref={moreMenuRef} className="relative hidden lg:block">
            <button
              onClick={() => setMoreMenuOpen(!moreMenuOpen)}
              className="flex items-center space-x-2 mx-4 text-sm font-medium text-gray-700 hover:text-red-500 transition"
            >
              <span>More</span>
              <FaChevronDown className={`text-xs transition-transform ${moreMenuOpen ? 'rotate-180' : ''}`} />
            </button>
            {moreMenuOpen && (
              <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50">
                <Link
                  to="/calculators"
                  onClick={() => setMoreMenuOpen(false)}
                  className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-red-50 hover:text-red-500 transition font-semibold"
                >
                  EMI Calculator
                </Link>
                <Link
                  to="/contact"
                  onClick={() => setMoreMenuOpen(false)}
                  className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-red-50 hover:text-red-500 transition"
                >
                  Contact Us
                </Link>
                <Link
                  to="/about"
                  onClick={() => setMoreMenuOpen(false)}
                  className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-red-50 hover:text-red-500 transition"
                >
                  About Us
                </Link>
                <Link
                  to="/privacy-policy"
                  onClick={() => setMoreMenuOpen(false)}
                  className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-red-50 hover:text-red-500 transition"
                >
                  Privacy Policy
                </Link>
              </div>
            )}
          </div>


          {/* Mobile menu toggle */}
          <button className="lg:hidden text-gray-700" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <FaTimes size={22} /> : <FaBars size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="lg:hidden bg-white border-t shadow-lg">
          <div className="px-4 py-4 space-y-3">
            {/* Mobile Search */}
            <form onSubmit={handleSearchSubmit} className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => {
                  if (searchQuery.trim().length === 0 && defaultResults.length > 0) {
                    setSearchResults(defaultResults);
                    setShowSearchResults(true);
                  } else if (searchResults.length > 0) {
                    setShowSearchResults(true);
                  }
                }}
                placeholder="Search properties..."
                className="w-full pl-10 pr-20 py-2.5 bg-gray-50 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              />
              <button
                type="submit"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-full text-xs font-semibold transition"
              >
                Search
              </button>
            </form>

            {/* Mobile Search Results */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="bg-gray-50 rounded-xl p-2 space-y-2 max-h-64 overflow-y-auto">
                {searchResults.map((property) => (
                  <button
                    key={property._id}
                    onClick={() => {
                      handleSelectProperty(property);
                      setIsOpen(false);
                    }}
                    className="w-full px-3 py-2 hover:bg-white transition rounded-lg flex items-start space-x-3 text-left"
                  >
                    <PropertyImage property={property} className="w-12 h-12 flex-shrink-0 rounded overflow-hidden" imgClassName="w-full h-full object-cover" />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-xs font-semibold text-gray-900 truncate">{property.title}</h4>
                      {property.location && (
                        <p className="text-xs text-gray-600 truncate">{property.location}</p>
                      )}
                      {property.price && (
                        <p className="text-xs font-bold text-red-500">₹{property.price}</p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {user ? (
              <div className="border-t pt-3 mt-2">
                <div className="flex items-center space-x-2 mb-3">
                  <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                    {user.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{user.name}</p>
                    <p className="text-xs text-gray-500">{user.email || user.phone}</p>
                  </div>
                </div>
                <Link
                  to="/profile"
                  onClick={() => setIsOpen(false)}
                  className="w-full bg-gray-100 text-gray-700 py-2.5 rounded-full text-sm font-semibold mb-2 block text-center"
                >
                  My Profile
                </Link>
                <button
                  onClick={() => { onLogout(); setIsOpen(false); }}
                  className="w-full bg-gray-100 text-red-500 py-2.5 rounded-full text-sm font-semibold"
                >
                  Logout
                </button>
              </div>
            ) : (
              <>
                <button
                  onClick={() => { onLoginClick(); setIsOpen(false); }}
                  className="w-full border-2 border-red-500 text-red-500 py-2.5 rounded-full text-sm font-semibold"
                >
                  Login
                </button>
                <button
                  onClick={() => { onSignUpClick(); setIsOpen(false); }}
                  className="w-full bg-red-500 text-white py-2.5 rounded-full text-sm font-semibold mt-2"
                >
                  Sign Up Free
                </button>
              </>
            )}

            {/* More Links */}
            <div className="border-t pt-3 mt-3 space-y-2">
              <Link
                to="/calculators"
                onClick={() => setIsOpen(false)}
                className="block text-sm font-semibold text-red-500 hover:text-red-600 py-2 transition"
              >
                EMI Calculator
              </Link>
              <Link
                to="/contact"
                onClick={() => setIsOpen(false)}
                className="block text-sm text-gray-700 hover:text-red-500 py-2 transition"
              >
                Contact Us
              </Link>
              <Link
                to="/about"
                onClick={() => setIsOpen(false)}
                className="block text-sm text-gray-700 hover:text-red-500 py-2 transition"
              >
                About Us
              </Link>
              <Link
                to="/privacy-policy"
                onClick={() => setIsOpen(false)}
                className="block text-sm text-gray-700 hover:text-red-500 py-2 transition"
              >
                Privacy Policy
              </Link>
            </div>
            {/* Bookmarks Icon - Visible on mobile */}
            <Link
              to="/profile"
              className="flex items-center space-x-1.5 text-sm font-medium text-gray-700 hover:text-red-500 transition relative"
            >
              <FaBookmark className="text-red-500" size={20} />
              {user && bookmarkCount > 0 && (
                <span className="relative -top-2 -right-[-16px] bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {bookmarkCount}
                </span>
              )}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
